<!--
  Preloading strategy:
    - loading can empirically take more than 1s
    - UI not polished enough → could use some refactoring here
-->
<template>
  <div
    :class="[
      'page-list',
      'page-list--structure-contract-template'
    ]"
  >

    <ui-link-button-meek
      :size="UI.SIZE.SMALL"
      :route="companyListRoute"
    >
      <ui-icon name="navigate_before" />
      Modèles de contrat de &laquo;&nbsp;{{ fromCompanyLevel().company.raison_sociale }}&nbsp;&raquo;
    </ui-link-button-meek>

    <ui-cloak
      :is-visible="fetchState.status.value === 'success'"
    >
      <template #cloak>
        <default-placeholder
          class="page-list__default-list"
        />
      </template>
      <template #default>
        <default-list
          v-bind="{
            typeProfessionList,
            defaultCollection,
            contractListPerCodeTypeProfession,
            asDefaultCallback,
            resetDefaultCallback: () => {},
          }"
          class="page-list__default-list"
        />
      </template>
    </ui-cloak>

    <ui-cloak
      :is-visible="fetchState.status.value === 'success'"
    >
      <template #cloak>
        <main-placeholder
          class="page-list__contact-list"
        />
      </template>
      <template #default>
        <main-list
          v-bind="{
            typeProfessionList,
            listCallback,
            duplicateCallback,
            asDefaultCallback,
            defaultCollection,
          }"
          :class="[
            'page-list__contract-list',
            isPending ? 'page-list__contract-list--busy' : ''
          ]"
        />
      </template>
    </ui-cloak>

  </div>

  <nuxt-page />

</template>


<style lang="sass" scoped>
@import ~/assets/sass/modules/layout/variables
@import '~/assets/sass/modules/layout/members'
@import ~/assets/sass/modules/hamac/variables

.page-list
  $self: &

  padding-top: $layout-spacer-y * .75
  padding-left: $layout-spacer-x
  padding-right: $layout-spacer-x
  padding-bottom: $layout-spacer-y

  //////////////////////////////////

  &__default-list
    width: $contract-template-default-list-width
    margin-top: $layout-spacer-y * .75
    margin-bottom: $layout-spacer-y

  &__contract-list--busy,
  &__default-list--busy
    @include no-interaction
    opacity: .6
    transition: opacity 0.3s
    transition-delay: opacity 0.3s

  //////////////////////////////////

  @include precede
    .overview-placeholder,
    .default-list,
    .contract-template-list
      border-color: $contract-template-structure-body

    :deep(.overview-placeholder__header),
    :deep(.default-list__header),
    :deep(.contract-template-list__header)
      background-color: $contract-template-structure-body

</style>

<script lang="ts">
import DefaultList from '~/components/hamac/contract-template/list/structure/DefaultList.vue'
import DefaultPlaceholder from '~/components/hamac/contract-template/list/DefaultPlaceholder.vue'
import MainList from '~/components/hamac/contract-template/list/structure/MainList.vue'
import MainPlaceholder from '~/components/hamac/contract-template/list/MainPlaceholder.vue'
import Console from '~/utils/lib/core/console'
import { clone, noop, tapCatch, toString } from '~/utils/lib/core/prelude'
import { getDetailsRoute } from '~/stores/hamac/contract-template/structure/index'
import { getListRoute as getCompanyListRoute } from '~/stores/hamac/contract-template/company/index'
import { groupByCodeTypeProfession, type ContractTemplate } from '~/stores/hamac/contract-template'
import { MENU_ITEM, NAVIGATION_SCOPE } from '~/utils/lib/layout/types'
import { useLegacyPageFetch } from '~/composables/core/hook'
import { useToastBuilder } from '~/composables/layout/toast'
import { useCompanyRequiredNavigationContext } from '~/composables/layout/navigation'
import { useAccessControl } from '~/composables/layout/access-control'
import { useLinkHandler } from '~/composables/core/link-handler'
import { useStore_hamac_contractTemplate_structure_list } from '~/stores/hamac/contract-template/structure/list'
import type { TypeProfession } from '~/stores/hamac/profession'
import { useStore_hamac_profession_typeProfession, type TypeProfessionHasDefaultContractTemplate } from '~/stores/hamac/profession/type-profession'
import { useStore_hamac_contractTemplate_structure_details } from '~/stores/hamac/contract-template/structure/details'
import type { ListInputSchema } from '~/utils/infra/common/types_'

const console = new Console('component:contract-list')

export default defineNuxtComponent({
  components: { DefaultList, MainList, MainPlaceholder, DefaultPlaceholder },
  setup() {
    definePageMeta({
      navigationScope: NAVIGATION_SCOPE.COMPANY_REQUIRED,
      menuItem: MENU_ITEM.EMBAUCHE,
    })
    const navigationContext = useCompanyRequiredNavigationContext()

    useHead({
      title: `Modèles de contrat — ${navigationContext.fromCompanyLevel().company.raison_sociale}`,
    })

    const {
      fetchState,
      fetchWrapper,
    } = useLegacyPageFetch()

    const ToastBuilder = useToastBuilder()

    const store_contractTemplate_structure_list = useStore_hamac_contractTemplate_structure_list()
    const store_contractTemplate_structure_details = useStore_hamac_contractTemplate_structure_details()
    const store_typeProfession = useStore_hamac_profession_typeProfession()

    return {
      ...navigationContext,
      ...useAccessControl(),
      ...useLinkHandler(),
      fetchState,
      fetchWrapper,
      ToastBuilder,
      store_contractTemplate_structure_list,
      store_contractTemplate_structure_details,
      store_typeProfession,
    }
  },
  data() {
    const {
      structure: { id },
      company: { code_societe }
      // @ts-expect-error: "data" hook not injecting "setup" dependencies
    } = this.fromCompanyLevel()

    const companyListRoute = getCompanyListRoute(id, code_societe)

    return {
      companyListRoute,
      contractListPerCodeTypeProfession: {} as Record<TypeProfession["code"], ContractTemplate[]>, // (lazy)
      typeProfessionList: [] as TypeProfession[], // (lazy)
      defaultCollection: {} as TypeProfessionHasDefaultContractTemplate, // (lazy)
      isPending: false,
    }
  },
  created() {
    this.fetchWrapper(
      () => this.refreshAll()
    )
  },
  methods: {
    listCallback(input) {
      const options = {
        uuidData: this.fromCompanyLevel().structure.uuidData,
        code_societe: this.fromCompanyLevel().company.code_societe,
        input,
      }

      return this.store_contractTemplate_structure_list.read(options)
        .catch(tapCatch(err => {
          console.warn('listCallback', err)
          this.ToastBuilder
            .error(toString(err))
            .title(`Recherche de modèles de contrat`)
            .show()
        }))
    },
    duplicateCallback(item) {
      const options = {
        uuidData: this.fromCompanyLevel().structure.uuidData,
        code_societe: this.fromCompanyLevel().company.code_societe,
        item,
      }

      return this.store_contractTemplate_structure_details.duplicate(options)
        .then(({ id }) => {
          const {
            structure: { id: structure_id },
            company: { code_societe }
          } = this.fromCompanyLevel()

          const url = getDetailsRoute(structure_id, code_societe, id)

          this.goToRoute(url)
            .then(
              () => this.ToastBuilder
                .success()
                .class('duplicate-model-toast')
                .title(`Modèle de contrat dupliqué`)
                .show()
            )
            .catch(noop)
        })
        .catch(err => {
          console.warn('duplicateCallback', err)
          this.ToastBuilder
            .error(toString(err))
            .class('duplicate-model-toast')
            .title(`Duplication de modèle`)
            .show()
        })
    },
    asDefaultCallback(item, code_type_profession) {
      const options = {
        uuidData: this.fromCompanyLevel().structure.uuidData,
        code_societe: this.fromCompanyLevel().company.code_societe,
        item,
        code_type_profession
      }

      return this.store_contractTemplate_structure_details.asDefault(options)
        .then(() => {
          // detached synchronisation
          this.refreshAll()
        })
        .catch(tapCatch(err => {
          console.warn('asDefaultCallback', err)
          this.ToastBuilder
            .error(toString(err))
            .title(`Modèle par défaut`)
            .show()
        }))
    },
    refreshAll() {
      const options = {
        uuidData: this.fromCompanyLevel().structure.uuidData,
        code_societe: this.fromCompanyLevel().company.code_societe,
      }

      const contractTemplateInput = {
        per_page: 10000, // eager
        order: 'updated_at',
        sort: 'desc',
      } satisfies ListInputSchema

      this.isPending = true

      return Promise.all([
          this.store_contractTemplate_structure_list.read(
            { ...options, input: contractTemplateInput }
          ),
          this.store_typeProfession.read_(
            options
          ),
          this.store_contractTemplate_structure_list.defaultCollection(
            options
          ),
        ])
        .then(results => {
          this.contractListPerCodeTypeProfession = groupByCodeTypeProfession(
            results[0].data
          )

          this.typeProfessionList = clone(results[1])

          this.defaultCollection = clone(results[2])
        })
        .catch(tapCatch(err => {
          console.warn('fetch', err)
          this.ToastBuilder
            .error(toString(err))
            .title(`Liste modèles de contrat`)
            .show()
        }))
        .finally(() => this.isPending = false)
    }
  }
})
</script>
